import { EmptyFunction, EmptyObject } from './types';
import { DESKTOP_ANALYTICS_EVENTS } from './desktop-tools/declarations';
import { desktopAnalyticsCollector } from './desktop-tools/desktop-analytics-collector';
import { isBrowser } from '@ringcentral/rwc-logger/src/utils';

export const Client = {
    CHROME: 'chrome',
    FF: 'firefox',
    SAFARI: 'safari',
    OPERA: 'opera',
    EDGE: 'edge',
    EDGE_CHROMIUM: 'edge_chromium',
    SAMSUNG: 'samsung',
    IE: 'ie',
    APPLICATION: 'application',
};

export const OS = {
    LINUX: 'linux',
    WIN: 'win',
    MACOS: 'macos',
    IOS: 'ios',
    ANDROID: 'android',
    UNKNOWN: 'unknown',
};

const WINDOWS_PLATFORM_ARCHITECTURE = {
    X86_64: 'x86_64',
    X86: 'x86',
    ARM: 'arm',
    ARM64: 'arm64',
    ARM32: 'arm32',
    UNKNOWN: 'unknown',
};

const PLATFORM_BITNESS = {
    ARCH_64: '64',
    ARCH_32: '32',
    UNKNOWN: 'unknown',
};

// TODO: Get RWC version dynamically in 23.1.30+ releases https://jira.ringcentral.com/browse/RCV-84370
export const UA_WEB = 'rcv/web/23.1.30';
export const UA_PSTN = 'rcv/pstn';
export const UA_DESKTOP = 'rcv/desktop/23.1.30';

export const isUserAgentDesktop = (userAgent: string) => userAgent.includes('rcv/desktop');
export const isUserAgentNativeClient = (userAgent: string) =>
    userAgent.includes('rcv/nativeclient');

export const getWindowObj = (): Window | EmptyObject => {
    if (typeof window === 'undefined') {
        return {};
    } else {
        return window;
    }
};

export const getNavigator = (): Navigator | EmptyObject => {
    return getWindowObj().navigator || {};
};

const fakeVer = ['0', '0'];

export const getOS = (navigator: Navigator | EmptyObject): string => {
    const android = /android/i.test(navigator.userAgent);
    const linux = /linux/i.test(navigator.platform);
    const mac = /Macintosh|MacIntel|MacPPC|Mac68K/i.test(navigator.platform);
    const ios =
        /ipad|iphone|ipod/i.test(navigator.userAgent) || (mac && navigator.maxTouchPoints > 0);
    const win = /Win64|Win32|Windows|WinCE/i.test(navigator.platform);

    switch (true) {
        case android:
            return OS.ANDROID;
        case ios:
            return OS.IOS;
        case linux:
            return OS.LINUX;
        case mac:
            return OS.MACOS;
        case win:
            return OS.WIN;
        default:
            return OS.UNKNOWN;
    }
};

export const isElectron = () => {
    // Renderer process
    if (
        typeof window !== 'undefined' &&
        typeof window.process === 'object' &&
        window.process.type === 'renderer'
    ) {
        return true;
    }

    const navigator = getNavigator();
    // Detect the user agent when the `nodeIntegration` option is set to true
    return Boolean(navigator.userAgent?.indexOf?.('Electron') >= 0);
};

const electronWarning = () => {
    throw new Error('available in Electron only');
};

export const electronOnly = <Type>(func: Type): Type | EmptyFunction =>
    isElectron() ? func : electronWarning;

/**
 * access sdk release version that was set by electron client
 */
export const getElectronSdkReleaseVersion = (): string => {
    return globalThis?._electronAPI?.getVersion?.() || '0';
};

/**
 * access sdk version that was set by electron client
 */
export const getElectronSdkVersion = (): string =>
    globalThis._electronAPI?.getSdkVersion?.() || '0';

/**
 * access electron version that was set by electron client
 */
export const getElectronVersion = (): string => {
    const sdkInfo = globalThis?._electronAPI?.getElectronVersion?.();
    // since dsdk 21.2.20 getElectronVersion returns an object instead of string
    const sdkElectronVersion = typeof sdkInfo === 'string' ? sdkInfo : sdkInfo?.version;
    const userAgentElectronVersion = navigator.userAgent.match(/Electron\/([\d.]+)/)?.[1];

    return sdkElectronVersion || userAgentElectronVersion || '0';
};

export const getElectronKind = electronOnly(() => {
    const navigator = getNavigator();
    const electronVersion = navigator.userAgent.match(/Electron\/([\d.]+)/)?.[1];

    return electronVersion ? electronVersion.split('.')[0] : 0;
});

export const getElectronArch = electronOnly(() => globalThis?._electronAPI?.getOSInfo?.().arch);

const getChromeVersion = navigator => {
    const matchedVersion = navigator.appVersion.match(/(?:(Chrome|CriOS)\/)(\d+(\.\d+)*)/);
    return matchedVersion ? matchedVersion[2] : fakeVer[1];
};

/**
 * @typedef {Object} ClientInfo
 * @property {String} name client name
 * @property {String} version client version
 * @property {String} [chromeVersion] chromium version for client (used for electron)
 */
export type ClientInfo = {
    name: string | undefined;
    version?: string;
    chromeVersion?: string;
};

export const getClient = (navigator: Navigator | EmptyObject): ClientInfo => {
    if (isElectron()) {
        return {
            name: Client.APPLICATION,
            version: getElectronSdkReleaseVersion(),
            chromeVersion: getChromeVersion(navigator),
        };
    }
    switch (navigator.vendor) {
        case 'Google Inc.':
            switch (true) {
                case /Opera|OPR\//.test(navigator.userAgent):
                    return {
                        name: Client.OPERA,
                        version: fakeVer[1],
                    };

                case /Edg/.test(navigator.userAgent):
                    return {
                        name: Client.EDGE_CHROMIUM,
                        version: getChromeVersion(navigator),
                    };

                case /SamsungBrowser/i.test(navigator.userAgent):
                    return {
                        name: Client.SAMSUNG,
                        version: fakeVer[1],
                    };

                default:
                    return {
                        name: Client.CHROME,
                        version: getChromeVersion(navigator),
                    };
            }

        case 'Apple Computer, Inc.':
            switch (true) {
                case /CriOS/i.test(navigator.userAgent):
                    return {
                        name: Client.CHROME,
                        version: getChromeVersion(navigator),
                    };
                default:
                    return {
                        name: Client.SAFARI,
                        version: (navigator.appVersion.match(/(?:Version\/)(\d+(\.\d+)*)/) ||
                            fakeVer)[1],
                    };
            }
        case 'Microsoft':
        case '':
            if (typeof window === 'object' && 'ActiveXObject' in window) {
                return {
                    name: Client.IE,
                    version: fakeVer[1],
                };
            }
            if (/Edge/.test(navigator.userAgent)) {
                return {
                    name: Client.EDGE,
                    version: fakeVer[1],
                };
            }
            return {
                name: Client.FF,
                version: (navigator.userAgent.match(/(?:Firefox\/)(\d+(\.\d+)*)/) || fakeVer)[1],
            };
    }
    return {
        name: undefined,
    };
};

export const UA_INFO = getClient(getNavigator());
export const OS_INFO = getOS(getNavigator());

export const isMac = OS_INFO === OS.MACOS;
export const isWin = OS_INFO === OS.WIN;
export const isMobile = OS_INFO === OS.IOS || OS_INFO === OS.ANDROID;
export const isIOS = OS_INFO === OS.IOS;
export const isAndroid = OS_INFO === OS.ANDROID;

export const isCatalina = (): boolean => {
    const catalinaVersion = '10.15';
    return isMac && compareVersions(getOsVersion(), catalinaVersion) >= 0;
};

export const isSequoia = (): boolean => {
    const sequoiaVersion = '15.0';
    return isMac && compareVersions(getOsVersion(), sequoiaVersion) >= 0;
};

// null - can't determine
export const isWinE27Arm = (): boolean | null =>
    isElectron() &&
    isWin &&
    compareVersions(getElectronVersion(), '28') <= 0 &&
    (getElectronArch() ? getElectronArch() === 'arm64' : null);

const getNumericVersion = (v, defaultV = 0) =>
    typeof v === 'string' && v.length > 0 ? Number(v) : defaultV;

/**
 * Compare 2 versions. Returns 0 is equal, -1 if 1st is less, 1 if 1st is larger
 * @param {String|Number} v1 1st version to compare
 * @param {String|Number} v2 2nd version to compare
 * @param {Number} fillEmptyV1 filler for v1 (when v2 has more dots)
 * @param {Number} fillEmptyV2 filler for v2 (when v1 has more dots)
 */
export const compareVersions = (
    v1: IntervalVersion,
    v2: IntervalVersion,
    fillEmptyV1 = 0,
    fillEmptyV2 = 0
): number => {
    // should work with numeric values
    const splittedV1 = ('' + v1).split('.');
    const splittedV2 = ('' + v2).split('.');
    const maxLength = Math.max(splittedV1.length, splittedV2.length);

    for (let i = 0; i < maxLength; i++) {
        const v1Subversion = getNumericVersion(splittedV1[i], fillEmptyV1);
        const v2Subversion = getNumericVersion(splittedV2[i], fillEmptyV2);

        if (v1Subversion > v2Subversion) {
            return 1;
        } else if (v1Subversion < v2Subversion) {
            return -1;
        }
    }

    return 0;
};

export const afterElectronVersion = (
    version: string,
    currentVersion = getElectronVersion(),
    _isElectron = isElectron(),
    _comparator = compareVersions
) => _isElectron && _comparator(currentVersion, version) >= 0;

type IntervalVersion = null | undefined | string | number;
type Interval = Array<IntervalVersion>;

export const checkIsVersionInInterval = (v: IntervalVersion, [vFrom, vUpTo]: Interval): boolean =>
    (vFrom === null || vFrom === undefined || compareVersions(v, vFrom) >= 0) &&
    (vUpTo === null || vUpTo === undefined || compareVersions(v, vUpTo, 0, Infinity) <= 0);

const checker = v => interval => checkIsVersionInInterval(v, interval);
/**
 * Check if version is in interval
 * @param {Number|String} v version to check
 * @param {Array} intervals versions interval. Example: [[null, '1.2.3'],['2.3.4','3.4.5'],['4.5.6']]
 */
export const isVersionInIntervals = (
    v: number | string | undefined,
    intervals: Array<Interval> = []
): boolean => intervals.some(checker(v));

/**
 * Returns client checker function for specified client
 * @param {string} hypothesisName the client to check
 * @param {Object} actual client description object
 * @returns checker function that accepts version boundaries
 */
export const checkClient =
    (hypothesisName: string, actual: ClientInfo) =>
    (intervals?: Array<Interval>): boolean =>
        hypothesisName === actual.name &&
        (!intervals || isVersionInIntervals(actual.version, intervals));

export const isChrome = checkClient(Client.CHROME, UA_INFO);
export const isFF = checkClient(Client.FF, UA_INFO);
export const isSafari = checkClient(Client.SAFARI, UA_INFO);
export const isIE = checkClient(Client.IE, UA_INFO);
export const isEdge = checkClient(Client.EDGE, UA_INFO);
export const isEdgeChromium = checkClient(Client.EDGE_CHROMIUM, UA_INFO);
export const isApplication = checkClient(Client.APPLICATION, UA_INFO);
export const isAndroidChrome = isAndroid && isChrome();
export const isDesktopSafari = !isMobile && isSafari();

// Engine-level WebKit detection via a Safari/WebKit-only API (multi-touch GestureEvent,
// not implemented in Blink/Gecko or in their BrowserEngineKit iOS variants under EU DMA).
export const isWebKit = 'GestureEvent' in getWindowObj();
export const isIOSWebKit = isIOS && isWebKit;

export const getBrowserName = () => {
    const { name } = UA_INFO;
    if (!name) return '';
    switch (name) {
        case Client.EDGE:
        case Client.EDGE_CHROMIUM:
            return 'Microsoft Edge';
        default:
            return name[0].toUpperCase() + name.slice(1);
    }
};

export const isE2eeSupported = (): boolean =>
    typeof RTCRtpSender !== 'undefined' &&
    // eslint-disable-next-line no-prototype-builtins
    RTCRtpSender.prototype.hasOwnProperty('createEncodedStreams');

export const isLandscape = () =>
    'orientation' in screen
        ? Boolean(screen.orientation.type.match('^landscape'))
        : window.innerWidth > window.innerHeight;

let platformVersionGlobal: string;
async function setPlatformVersion() {
    if (platformVersionGlobal || !navigator['userAgentData']) return;
    try {
        const values = await navigator['userAgentData'].getHighEntropyValues(['platformVersion']);
        platformVersionGlobal = values.platformVersion;
    } catch (error) {
        // cannot use rwc logger here because of circular dependency
        console.error('Cannot set platformVersion', error);
    }
}

let osVersionFromDesktopGlobal: string;
function setOsVersionFromDesktop() {
    if (osVersionFromDesktopGlobal) return;
    desktopAnalyticsCollector.once(DESKTOP_ANALYTICS_EVENTS.CLIENT_INFO, info => {
        try {
            osVersionFromDesktopGlobal = info?.device?.os_version.split(' ')[0];
        } catch (error) {
            // cannot use rwc logger here because of circular dependency
            console.error('Cannot set osVersionFromDesktop', error);
        }
    });
}

if (isApplication() && desktopAnalyticsCollector) {
    setOsVersionFromDesktop();
} else {
    setPlatformVersion();
}

export const getOsVersion = (
    osName = OS_INFO,
    navigator = getNavigator(),
    versionFromDesktop = osVersionFromDesktopGlobal,
    platformVersion = platformVersionGlobal
): string | undefined => {
    const getMacOsVersion = () => {
        if (versionFromDesktop) return versionFromDesktop;
        if (platformVersion) return platformVersion;
        return navigator.userAgent.match(/OS X (\d*[_.]\d*)/)?.[1].replace('_', '.');
    };

    const getWindowsVersion = () => {
        // https://learn.microsoft.com/en-us/windows/win32/sysinfo/operating-system-version
        const releaseToVersion = {
            '6.1': '7',
            '6.2': '8',
            '6.3': '8.1',
            '10.0': '10',
        };
        if (versionFromDesktop) {
            // currently desktop returns us OS release version, so we need to map it into OS version
            // https://learn.microsoft.com/en-us/windows/release-health/windows11-release-information
            if (compareVersions(versionFromDesktop, '10.0.22000') >= 0) return '11';
            const semver = versionFromDesktop.split('.').map(Number);
            return releaseToVersion[semver.slice(0, 2).join('.')];
        }
        if (platformVersion) {
            // https://learn.microsoft.com/en-us/microsoft-edge/web-platform/how-to-detect-win11
            const majorPlatformVersion = parseInt(platformVersion.split('.')[0]);
            if (majorPlatformVersion >= 13) return '11';
            if (majorPlatformVersion > 0) return '10';
        }
        const uaVersion = navigator.userAgent.match(/Windows NT (\d*\.\d*)/)?.[1];
        if (!uaVersion) return uaVersion;
        return releaseToVersion[uaVersion] || uaVersion;
    };

    switch (osName) {
        case OS.MACOS:
            return getMacOsVersion();
        case OS.WIN:
            return getWindowsVersion();
        default:
            return 'unknown';
    }
};

// Safari and FF doesn't allow trigger getDisplayMedia by code directly,
// only in real user action's callback.
export const getIsOrderScreenShareSupported = () => !(isFF() || isSafari());

// https://learn.microsoft.com/en-us/microsoft-edge/web-platform/how-to-detect-win11
const getWindowsArchitecture = async () => {
    try {
        if (
            !navigator['userAgentData'] ||
            typeof navigator['userAgentData'].getHighEntropyValues !== 'function'
        ) {
            return WINDOWS_PLATFORM_ARCHITECTURE.UNKNOWN;
        }

        const ua = await navigator['userAgentData'].getHighEntropyValues([
            'architecture',
            'bitness',
        ]);

        if (!ua || !ua.architecture || !ua.bitness) {
            return WINDOWS_PLATFORM_ARCHITECTURE.UNKNOWN;
        }

        const architectureMap = {
            [WINDOWS_PLATFORM_ARCHITECTURE.X86]: {
                [PLATFORM_BITNESS.ARCH_64]: WINDOWS_PLATFORM_ARCHITECTURE.X86_64,
                [PLATFORM_BITNESS.ARCH_32]: WINDOWS_PLATFORM_ARCHITECTURE.X86,
            },
            [WINDOWS_PLATFORM_ARCHITECTURE.ARM]: {
                [PLATFORM_BITNESS.ARCH_64]: WINDOWS_PLATFORM_ARCHITECTURE.ARM64,
                [PLATFORM_BITNESS.ARCH_32]: WINDOWS_PLATFORM_ARCHITECTURE.ARM32,
            },
        };

        const architecture = architectureMap[ua.architecture]?.[ua.bitness];
        return architecture || WINDOWS_PLATFORM_ARCHITECTURE.UNKNOWN;
    } catch (error) {
        return WINDOWS_PLATFORM_ARCHITECTURE.UNKNOWN;
    }
};

let windowsArchitecture: string | null = null;

/**
 * Detects if the current device is running on Microsoft ARM processor in browser
 */
const initWindowsArchitecture = () => {
    if (isBrowser() && OS_INFO === OS.WIN) {
        getWindowsArchitecture().then(architecture => {
            windowsArchitecture = architecture;
            return architecture;
        });
    } else {
        windowsArchitecture = WINDOWS_PLATFORM_ARCHITECTURE.UNKNOWN;
    }
};

initWindowsArchitecture();

export const isMicrosoftARMinBrowser = (): boolean => {
    if (!isBrowser() || OS_INFO !== OS.WIN) {
        return false;
    }
    return windowsArchitecture === WINDOWS_PLATFORM_ARCHITECTURE.ARM64;
};
